<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Check, Copy, Database, KeyRound, Laptop, LogOut, MailCheck, RefreshCcw, ShieldCheck, Trash2, TriangleAlert } from '@lucide/vue'
import { ApiError, api, deleteJson, downloadJson, postJson, putJson, standaloneMode } from '../api'
import { auth, refreshAuth } from '../state'

interface SessionItem { id: string; user_agent: string | null; created_at: string; last_seen_at: string; expires_at: string; current: boolean }

const route = useRoute()
const router = useRouter()
const action = computed(() => String(route.params.action || ''))
const message = ref('')
const error = ref('')
const loading = ref(false)
const sessions = ref<SessionItem[]>([])
const developmentToken = ref(sessionStorage.getItem('lumi-development-verification') || '')
const setup = ref<{ secret: string; uri: string } | null>(null)
const totpCode = ref('')
const disableForm = ref({ password: '', code: '' })
const deleteForm = ref({ password: '', confirmation: '' })
const resetPassword = ref('')
const passwordForm = ref({ currentPassword: '', newPassword: '', confirmation: '' })
const changingPassword = ref(false)

async function handleLinkAction() {
  const token = String(route.query.token || '')
  if (!action.value || !token) return
  loading.value = true
  try {
    if (action.value === 'verify-email') {
      await postJson('/api/auth/email/verify', { token })
      message.value = '邮箱验证成功，你现在可以使用全部功能。'
      if (auth.user) await refreshAuth()
    } else if (action.value === 'guardian-consent') {
      await postJson('/api/auth/guardian/verify', { token })
      message.value = '监护确认已经记录。'
    }
  } catch (err) { error.value = err instanceof ApiError ? err.message : '链接处理失败。' }
  finally { loading.value = false }
}

async function loadSessions() {
  if (!auth.user || standaloneMode) return
  sessions.value = await api<SessionItem[]>('/api/account/sessions')
}

onMounted(async () => {
  await handleLinkAction()
  await loadSessions()
})

async function resendVerification() {
  const result = await postJson<{ developmentToken?: string }>('/api/auth/email/request-verification', {})
  if (result.developmentToken) {
    developmentToken.value = result.developmentToken
    sessionStorage.setItem('lumi-development-verification', result.developmentToken)
  }
  message.value = '验证邮件已经进入发送队列。'
}

async function verifyDevelopmentToken() {
  await postJson('/api/auth/email/verify', { token: developmentToken.value })
  sessionStorage.removeItem('lumi-development-verification')
  developmentToken.value = ''
  await refreshAuth()
  message.value = '邮箱验证成功。'
}

async function revokeSession(id: string) {
  await deleteJson(`/api/account/sessions/${id}`)
  await loadSessions()
}

async function startTwoFactor() {
  setup.value = await postJson('/api/account/2fa/setup', {})
}

async function enableTwoFactor() {
  await postJson('/api/account/2fa/enable', { code: totpCode.value })
  await refreshAuth(); setup.value = null; totpCode.value = ''; message.value = '两步验证已开启。'
}

async function disableTwoFactor() {
  await postJson('/api/account/2fa/disable', disableForm.value)
  await refreshAuth(); disableForm.value = { password: '', code: '' }; message.value = '两步验证已关闭。'
}

async function copy(value: string) {
  error.value = ''
  try {
    if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(value)
    else {
      const field = document.createElement('textarea')
      field.value = value
      field.setAttribute('readonly', '')
      field.style.position = 'fixed'
      field.style.opacity = '0'
      document.body.appendChild(field)
      field.select()
      const copied = document.execCommand('copy')
      field.remove()
      if (!copied) throw new Error('clipboard unavailable')
    }
    message.value = '已复制到剪贴板。'
  } catch {
    error.value = '浏览器未允许自动复制，请选中内容后手动复制。'
  }
}

async function resetFromLink() {
  error.value = ''
  try {
    await postJson('/api/auth/password/reset', { token: String(route.query.token || ''), password: resetPassword.value })
    message.value = '密码已重置，请使用新密码登录。'
    setTimeout(() => router.push('/login'), 900)
  } catch (err) { error.value = err instanceof ApiError ? err.message : '重置失败。' }
}

async function changePassword() {
  error.value = ''
  message.value = ''
  if (passwordForm.value.newPassword !== passwordForm.value.confirmation) {
    error.value = '两次输入的新密码不一致。'
    return
  }
  changingPassword.value = true
  try {
    await putJson('/api/account/password', {
      currentPassword: passwordForm.value.currentPassword,
      newPassword: passwordForm.value.newPassword,
    })
    passwordForm.value = { currentPassword: '', newPassword: '', confirmation: '' }
    await refreshAuth()
    message.value = '密码已经更新，其他设备上的登录已撤销。建议现在开启两步验证。'
    await router.replace('/account')
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : '密码修改失败，请稍后重试。'
  } finally {
    changingPassword.value = false
  }
}

async function deleteAccount() {
  error.value = ''
  try {
    await deleteJson('/api/account', deleteForm.value)
    auth.user = null
    await router.push('/')
  } catch (err) { error.value = err instanceof ApiError ? err.message : '注销失败。' }
}
</script>

<template>
  <div class="account-page section-pad">
    <section v-if="action === 'reset-password'" class="account-action-card">
      <KeyRound/><h1>设置新密码</h1><p>新密码至少 12 位，建议使用与其他网站不同的密码。</p>
      <label class="field-label"><span>新密码</span><input v-model="resetPassword" type="password" minlength="12" autocomplete="new-password"/></label>
      <p v-if="error" class="form-error">{{ error }}</p><p v-if="message" class="dashboard-message">{{ message }}</p>
      <button class="button button-dark" @click="resetFromLink">确认重置</button>
    </section>

    <section v-else-if="action" class="account-action-card">
      <MailCheck/><h1>{{ loading ? '正在验证…' : message ? '操作完成' : '链接无法使用' }}</h1>
      <p v-if="message">{{ message }}</p><p v-if="error" class="form-error">{{ error }}</p>
      <RouterLink class="button button-dark" :to="auth.user ? '/account' : '/login'">{{ auth.user ? '返回账号中心' : '前往登录' }}</RouterLink>
    </section>

    <template v-else-if="auth.user">
      <header class="account-header"><div><p class="eyebrow">ACCOUNT & PRIVACY</p><h1>账号、安全与<br/><em>数据权利</em></h1></div><div class="account-identity"><span>{{ auth.user.name.slice(0, 1) }}</span><div><strong>{{ auth.user.name }}</strong><small>{{ auth.user.email }}</small></div></div></header>
      <p v-if="message" class="dashboard-message">{{ message }}</p><p v-if="error" class="form-error">{{ error }}</p>
      <div v-if="auth.user.mustChangePassword" class="security-notice"><TriangleAlert/><p><strong>当前使用的是临时密码。</strong> 为保护后台数据，请先设置你自己的密码；完成前不能进入管理和内容工作台。</p></div>
      <div v-else-if="['admin', 'safety_admin', 'super_admin'].includes(auth.user.role) && !auth.user.totpEnabled" class="security-notice"><ShieldCheck/><p><strong>还需要开启两步验证。</strong> 完成设置后才能进入管理和内容工作台，避免密码泄露后后台被直接接管。</p></div>
      <div v-if="standaloneMode" class="security-notice"><TriangleAlert/><p>直接打开版只在当前浏览器保存演示数据，不提供服务器账号安全功能。公网完整版部署后本页功能才会生效。</p></div>

      <div v-else class="account-grid">
        <section class="account-panel wide-panel">
          <div class="panel-heading"><div><h2>{{ auth.user.mustChangePassword ? '立即更换临时密码' : '修改管理员密码' }}</h2><p>至少 12 位，并且不要与邮箱、网站名或其他网站密码相同</p></div><KeyRound/></div>
          <div class="compact-form password-change-form">
            <input v-model="passwordForm.currentPassword" type="password" autocomplete="current-password" placeholder="当前密码"/>
            <input v-model="passwordForm.newPassword" type="password" autocomplete="new-password" minlength="12" placeholder="新密码（至少 12 位）"/>
            <input v-model="passwordForm.confirmation" type="password" autocomplete="new-password" minlength="12" placeholder="再次输入新密码"/>
            <button class="button button-dark" :disabled="changingPassword" @click="changePassword">{{ changingPassword ? '正在更新…' : '保存新密码' }}</button>
          </div>
        </section>
        <section class="account-panel">
          <div class="panel-heading"><div><h2>邮箱验证</h2><p>用于找回密码与重要安全通知</p></div><MailCheck/></div>
          <div :class="['verification-state', { verified: auth.user.emailVerified }]"><Check v-if="auth.user.emailVerified"/><TriangleAlert v-else/><span>{{ auth.user.emailVerified ? '邮箱已验证' : '邮箱尚未验证，创作与提交功能受限' }}</span></div>
          <button v-if="!auth.user.emailVerified" class="button" @click="resendVerification"><RefreshCcw/>重新发送验证邮件</button>
          <div v-if="developmentToken && !auth.user.emailVerified" class="dev-token"><small>本地开发模式验证令牌</small><div><input v-model="developmentToken"/><button type="button" title="使用此令牌验证邮箱" aria-label="使用开发令牌验证邮箱" @click="verifyDevelopmentToken"><Check/></button></div></div>
        </section>

        <section class="account-panel">
          <div class="panel-heading"><div><h2>两步验证</h2><p>登录时增加动态验证码</p></div><ShieldCheck/></div>
          <template v-if="auth.user.totpEnabled">
            <p class="success-line"><Check/>已经开启 TOTP 两步验证</p>
            <div class="compact-form"><input v-model="disableForm.password" type="password" placeholder="当前密码"/><input v-model="disableForm.code" inputmode="numeric" maxlength="6" placeholder="6 位验证码"/><button class="button" @click="disableTwoFactor">关闭两步验证</button></div>
          </template>
          <template v-else-if="setup">
            <p>在密码管理器或身份验证器中添加以下密钥：</p><div class="secret-line"><code>{{ setup.secret }}</code><button type="button" title="复制两步验证密钥" aria-label="复制两步验证密钥" @click="copy(setup.secret)"><Copy/></button></div>
            <details><summary>显示 otpauth 地址</summary><code class="uri-code">{{ setup.uri }}</code></details>
            <div class="compact-form"><input v-model="totpCode" inputmode="numeric" maxlength="6" placeholder="输入生成的 6 位验证码"/><button class="button button-dark" @click="enableTwoFactor">验证并开启</button></div>
          </template>
          <button v-else class="button" @click="startTwoFactor"><KeyRound/>开始设置</button>
        </section>

        <section class="account-panel wide-panel">
          <div class="panel-heading"><div><h2>登录设备</h2><p>发现陌生设备时立即撤销</p></div><Laptop/></div>
          <div class="session-list"><article v-for="item in sessions" :key="item.id"><Laptop/><div><strong>{{ item.user_agent || '未知设备' }}</strong><small>最近活动：{{ new Date(item.last_seen_at).toLocaleString() }} · 到期：{{ new Date(item.expires_at).toLocaleDateString() }}</small></div><span v-if="item.current">当前设备</span><button v-else type="button" title="撤销此设备的登录状态" :aria-label="`撤销设备：${item.user_agent || '未知设备'}`" @click="revokeSession(item.id)"><LogOut/></button></article></div>
        </section>

        <section class="account-panel">
          <div class="panel-heading"><div><h2>导出数据</h2><p>下载账号、问卷与答卷副本</p></div><Database/></div>
          <button class="button" @click="downloadJson('/api/account/export', 'lumiscale-data-export.json')"><Database/>下载 JSON 副本</button>
        </section>

        <section v-if="!['admin', 'safety_admin', 'super_admin'].includes(auth.user.role)" class="account-panel danger-panel">
          <div class="panel-heading"><div><h2>注销账号</h2><p>个人答卷会匿名化，自有问卷将归档</p></div><Trash2/></div>
          <div class="compact-form"><input v-model="deleteForm.password" type="password" placeholder="当前密码"/><input v-model="deleteForm.confirmation" placeholder="输入：删除我的账号"/><button class="button danger-button" @click="deleteAccount"><Trash2/>永久注销</button></div>
        </section>
      </div>
    </template>
  </div>
</template>
